import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../api/client'
import { JobCard } from '../components/jobs'
import { Loading } from '../components/ui'
import { JobsSEO } from '../hooks/useSEO'
import type { Job } from '../types'
import { jobCategoriesFilter } from '../types'
import { useAuthStore } from '../stores/authStore'

interface JobsResponse {
  jobs: Job[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export default function JobsPage() {
  const { isAuthenticated } = useAuthStore()
  const [jobs, setJobs] = useState<Job[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    search: '',
    page: 1,
  })
  const [totalPages, setTotalPages] = useState(1)

  useEffect(() => {
    fetchJobs()
  }, [filters])

  const fetchJobs = async () => {
    setIsLoading(true)
    try {
      const params = new URLSearchParams()
      if (filters.status) params.append('status', filters.status)
      if (filters.category) params.append('category', filters.category)
      if (filters.search) params.append('search', filters.search)
      params.append('page', String(filters.page))
      params.append('limit', '12')

      const data = await api.get<JobsResponse>(`/jobs?${params}`)
      setJobs(data.jobs)
      setTotalPages(data.totalPages)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load jobs')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div>
      <JobsSEO />
      <div className="page-header">
        <div>
          <h1>Browse Jobs</h1>
          <p className="text-secondary">Find work opportunities and start earning USDC</p>
        </div>
        {isAuthenticated && (
          <Link to="/jobs/new" className="btn btn-primary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Post a Job
          </Link>
        )}
      </div>

      <div className="card-static" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <input
            type="text"
            className="input"
            placeholder="Search jobs..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
            style={{ flex: 1, minWidth: 200 }}
          />
          <select
            className="input select"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
            style={{ width: 150 }}
          >
            <option value="">All Status</option>
            <option value="OPEN">Open</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="COMPLETED">Completed</option>
          </select>
          <select
            className="input select"
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value, page: 1 })}
            style={{ width: 180 }}
          >
            {jobCategoriesFilter.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {isLoading ? (
        <Loading message="Loading jobs..." />
      ) : jobs.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
            </svg>
          </div>
          <h3>No jobs found</h3>
          <p>Try adjusting your filters or check back later</p>
        </div>
      ) : (
        <>
          <div className="jobs-grid">
            {jobs.map((job) => (
              <JobCard key={job.id} job={job} />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                disabled={filters.page === 1}
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
              >
                &lt;
              </button>
              {(() => {
                // Sliding window pagination: show 5 pages centered around current page
                const maxVisible = 5
                let start = Math.max(1, filters.page - Math.floor(maxVisible / 2))
                const end = Math.min(totalPages, start + maxVisible - 1)
                // Adjust start if we're near the end
                start = Math.max(1, end - maxVisible + 1)

                return Array.from({ length: end - start + 1 }, (_, i) => {
                  const pageNum = start + i
                  return (
                    <button
                      key={pageNum}
                      className={`pagination-btn ${filters.page === pageNum ? 'active' : ''}`}
                      onClick={() => setFilters({ ...filters, page: pageNum })}
                    >
                      {pageNum}
                    </button>
                  )
                })
              })()}
              <button
                className="pagination-btn"
                disabled={filters.page === totalPages}
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
              >
                &gt;
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
